import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Page from '../../../components/Page.js';
import MetaTags from '../../../components/MetaTags';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import { ProviderAPI } from '../../../api';
import { ProviderStore } from '../../../store';
import { connect } from 'react-redux';
import PageTitle from '../../../components/PageTitle';
import MainImage from '../components/MainImage';
import WidgetBar from '../components/WidgetBar';
import TabsPanel from '../components/TabsPanel';
import ProviderLinkSubtitle from '../components/ProviderLinkSubtitle';
import ListingInfoPanel from '../components/ListingInfoPanel';
import config from '../../../config';
import * as QueryString from 'query-string';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AccreditationBadge from '../components/AccreditationBadge';
import ActionButtons from '../components/ActionButtons';
import ServiceBadge from '../components/ServiceBadge';
import * as gtm from '../../../lib/gtm';
import * as track from '../../../lib/track';
import LoadingSkeleton from '../components/LoadingSkeleton';
import ClaimProfile from '../components/ClaimProfile';
import UserFeaturesCTA from '../../../components/widgets/UserFeaturesCTA';

const desktopStyles = makeStyles(() => ({
  leftPane: {
    paddingRight: '3rem',
  }
}));

const DesktopLayout = ({providerUuid, data, tab, handleTabChange}) => {
  const classes = desktopStyles();
  return (
    <Container maxWidth="lg" fixed disableGutters={true}>
      <Grid container spacing={6}>
        <Grid item md={8}>
          <div className={classes.leftPane}>
            <MainImage data={data} />
            <PageTitle text={data.title} />
            <ProviderLinkSubtitle
              href={`/provider/${data.provider_slug}`}
              name={data.provider_name}
            />
            <WidgetBar data={data} />
            <TabsPanel
              data={data}
              itemType="listing"
              showReviews={true}
              selectedTab={tab}
              handleChange={handleTabChange}
            />
          </div>
        </Grid>
        <Grid item md={4}>
          <div>
            <UserFeaturesCTA />
            <ClaimProfile
              providerUuid={providerUuid}
              claimed={data.claimed}
            />
            <AccreditationBadge data={data}/>
            <ActionButtons
              providerUuid={providerUuid}
              itemType="listing"
              data={data}
            />
            <ServiceBadge data={data} />
            <ListingInfoPanel data={data} />
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

DesktopLayout.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  tab: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
};

const MobileLayout = ({providerUuid, data, tab, handleTabChange}) => {
  return (
    <Container maxWidth="sm" disableGutters={true} style={{ overflowX: 'hidden' }}>
      <Grid container spacing={0}>
        <Grid item sm={12}>
          <div>
            <MainImage data={data} />
            <PageTitle text={data.title} />
            <ProviderLinkSubtitle
              href={`/provider/${data.provider_slug}`}
              name={data.provider_name}
            />
            <UserFeaturesCTA />
            <ClaimProfile
              providerUuid={providerUuid}
              claimed={data.claimed}
            />
            <AccreditationBadge data={data}/>
            <ActionButtons
              providerUuid={providerUuid}
              itemType="listing"
              data={data}
            />
            <WidgetBar data={data} />
            <ServiceBadge data={data} />
            <TabsPanel
              data={data}
              itemType="listing"
              showReviews={true}
              selectedTab={tab}
              handleChange={handleTabChange}
            />
            <ListingInfoPanel data={data} />
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

MobileLayout.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  tab: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
};


const commonStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    minHeight: '65vh',
    padding: '2rem 1rem',
  },
}));

const ListingPage = ({dispatch, match, data, isLoading, location}) => {
  const classes = commonStyles();
  const history = useHistory();
  const theme = useTheme();
  const uuidOrSlug = match.params.uuid;
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const query = QueryString.parse(location.search);
  const initialTab = (('tab' in query) ? parseInt(query.tab) : 1) - 1; // 1 indexed is easier for normal people
  const [tab, setTab] = React.useState(initialTab);

  const fetchListing = async () => {
    dispatch(ProviderStore.isLoading('listing', true));
    try {
      const { payload } = await ProviderAPI.getListing(uuidOrSlug);
      dispatch(ProviderStore.setListing(payload));
      gtm.sendEvent('listing', {
        listingUuid: payload.uuid,
        provider_uuid: payload.provider_uuid,
      });
      track.sendEvent('provider listing', 'view', {
        listing_uuid: payload.uuid,
        slug: payload.slug,
      }, [payload.provider_uuid]);
      history.replace('/listing/' + payload.slug + location.search);
    } catch (err) {
      if (err.status === 404) {
        // historical (wordpress) provider urls were /listing/<provider-slug>
        // Try resolving this slug as a provider.  If it fails it will redirect
        // again to a soft-404 page.
        history.replace('/provider/' + uuidOrSlug);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    track.sendEvent('provider listing tab', 'click', {value: newValue});
    history.push(`${location.pathname}?tab=${newValue+1}`);
  };

  // console.log(data);
  React.useEffect(() => {
    fetchListing();
  }, [uuidOrSlug]); // eslint-disable-line

  // required for browser back button to work with tabs
  React.useEffect(() => {
    setTab(initialTab);
  }, [initialTab]); // eslint-disable-line

  if (isLoading) {
    return (
      <Page title={`${data.title} - Clickability`}>
        <LoadingSkeleton />
      </Page>
    );
  }

  return (
    <Page title={`${data.title} - Clickability`}>
      <MetaTags
        article
        title={data.title}
        description={`Read ratings and reviews from the people who use ${data.title}. Clickability is an Australian disability service directory.`}
        image={data.image_path}
        url={`${config.website.URL}/listing/${data.slug}`}
      />
      <div className={classes.root}>
        {mobile ? (
          <MobileLayout
            providerUuid={data.provider_uuid}
            data={data}
            tab={tab}
            handleTabChange={handleTabChange}
          />
        ) : (
          <DesktopLayout
            providerUuid={data.provider_uuid}
            data={data}
            tab={tab}
            handleTabChange={handleTabChange}
          />
        )}
      </div>
    </Page>
  );

};

ListingPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
  return {
    data: state.provider.listing,
    isLoading: state.provider.listingLoading,
  };
};

export default connect(mapStateToProps)(ListingPage);
